import { equals, omit } from 'ramda'
import { useCallback, useState, useRef, useEffect, type SetStateAction } from 'react'
import { useCompatRouter } from 'shared/components/Router'
import { decodeQueryParameterValue, encodeQueryParameterValue } from 'shared/utils/queryParameterCodec'
import { type z } from 'zod'
import { isNilOrEmpty } from 'shared/utils'

const MAX_CHARACTERS_IN_QUERY_PARAMETER = 1600

interface OptionsType<T, U = T> {
  /**
   * If true, the query parameter will be removed from the URL when the state is set to the default value.
   * Defaults to true.
   */
  removeWhenDefault?: boolean
  /**
   * If true, empty values are removed from the URL.
   * Defaults to true. Set to false when an empty value differs meaningfully from the default.
   */
  removeWhenEmpty?: boolean
  /**
   * A schema that validates the query state
   */
  schema?: z.ZodType<U>
  /**
   * Transform function to convert from URL format to component state format
   */
  transformFromUrl?: (urlValue: U) => T
  /**
   * Transform function to convert from component state format to URL format
   */
  transformToUrl?: (stateValue: T) => U
}

/**
 * A hook that implements functionality similar to `useState`, but uses the URL query parameters as the source of truth.
 * It also updates the URL query parameters when the state is updated.
 * It's implemented using the `window.location.search` and `window.history.replaceState`, since Next.js router is async and can cause race conditions
 * and inconsistent state values.
 *
 * @param key The key of the query parameter.
 * @param defaultValue The default value to use if the query parameter is not present.
 * @param options Additional options including transformers.
 * @returns A stateful value, and a function to update it.
 */
export function useQueryParameterState<T, U = T>(
  key: string,
  defaultValue: T,
  options?: OptionsType<T, U>
): [T, (value: SetStateAction<T>) => void]

/**
 * A hook that implements functionality similar to `useState`, but uses the URL query parameters as the source of truth.
 * It also updates the URL query parameters when the state is updated.
 * It's implemented using the `window.location.search` and `window.history.replaceState`, since Next.js router is async and can cause race conditions
 * and inconsistent state values.
 *
 * @param key The key of the query parameter.
 * @param options Additional options including transformers.
 * @returns A stateful value (which could be undefined), and a function to update it.
 */
export function useQueryParameterState<T, U = T>(
  key: string,
  defaultValue?: never,
  options?: OptionsType<T, U>
): [T | undefined, (value: SetStateAction<T | undefined>) => void]

/**
 * A hook that implements functionality similar to useState, but uses the URL query parameters as the source of truth.
 * It also updates the URL query parameters when the state is updated.
 * It's implemented using the window.location.search and window.history.replaceState, since next router is async and can cause race conditions
 * and inconsistent state values
 *
 */
export function useQueryParameterState<T, U = T>(
  key: string,
  defaultValue?: T,
  options?: OptionsType<T, U>
): [T | undefined, (value: SetStateAction<T | undefined>) => void] {
  const router = useCompatRouter()
  const { removeWhenDefault = true, removeWhenEmpty = true, schema, transformFromUrl, transformToUrl } = options ?? {}
  // Use a ref to store the latest defaultValue to avoid recreating parseAndSetValue
  const defaultValueRef = useRef(defaultValue)
  useEffect(() => {
    defaultValueRef.current = defaultValue
  }, [defaultValue])

  const transformFromUrlRef = useRef(transformFromUrl)
  transformFromUrlRef.current = transformFromUrl

  const parseAndSetValue = useCallback(
    (queryValue: string | string[] | null): T | undefined => {
      if (queryValue == null) return defaultValueRef.current

      try {
        const parsed = decodeQueryParameterValue(Array.isArray(queryValue) ? queryValue[0] : queryValue)

        // Validate against schema if provided
        if (schema != null) {
          const validation = schema.safeParse(parsed)
          if (!validation.success) return defaultValueRef.current
          const validatedData = validation.data

          return transformFromUrlRef.current?.(validatedData) ?? (validatedData as unknown as T)
        }

        return parsed as T
      } catch (error) {
        return defaultValueRef.current
      }
    },
    [schema]
  )

  const [value, setValue] = useState<T | undefined>(() => {
    const params = Object.fromEntries(new URLSearchParams(location.search))
    const urlValue = parseAndSetValue(params[key])
    return urlValue ?? defaultValue
  })

  // Track if the user has explicitly set a value (not just using default)
  const hasUserSetValue = useRef(false)

  // Use refs to track previous values and avoid infinite loops
  const prevDefaultValueRef = useRef(defaultValue)
  const valueRef = useRef(value)

  useEffect(() => {
    valueRef.current = value
  }, [value])

  // Sync state with defaultValue changes when user hasn't set a value
  useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(location.search))
    const hasUrlParam = params[key] != null
    const defaultChanged = !equals(prevDefaultValueRef.current, defaultValue)

    if (!defaultChanged || hasUserSetValue.current) {
      return
    }

    // Apply the new default when:
    // 1. There's no URL parameter, or
    // 2. We still have no usable value (e.g. invalid URL while default was bootstrapping)
    if (!hasUrlParam || valueRef.current === undefined) {
      setValue(defaultValue)
    }
    prevDefaultValueRef.current = defaultValue
  }, [defaultValue, key])

  useEffect(() => {
    // The internal Next.js router handles the URL change asynchronously,
    // so we need to listen to the routeChangeComplete event to update the state as well
    // This is especially important for any changes made through the "useChangePath" hook
    const handleRouteChange = (): void => {
      const params = Object.fromEntries(new URLSearchParams(window.location.search))
      const newValue = parseAndSetValue(params[key])
      if (!equals(newValue, valueRef.current)) {
        setValue(newValue)
      }
    }

    router?.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router?.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router, key, parseAndSetValue])

  // Re-parse when transformFromUrl changes (e.g. filter options loaded from async data).
  // Skip the first run — useState already decoded on mount; re-running would inflate twice.
  // Only applies when the URL already has this param — defaults are synced via the effect above.
  const isFirstTransformEffectRef = useRef(true)
  useEffect(() => {
    if (isFirstTransformEffectRef.current) {
      isFirstTransformEffectRef.current = false
      return
    }

    const params = Object.fromEntries(new URLSearchParams(window.location.search))
    if (params[key] == null) {
      return
    }

    const newValue = parseAndSetValue(params[key])
    if (!equals(newValue, valueRef.current)) {
      setValue(newValue)
    }
  }, [key, transformFromUrl, parseAndSetValue])

  const setValueAndQueryParameter: (newValue: SetStateAction<T | undefined>, shouldUpdateUrl?: boolean) => void =
    useCallback(
      (newValue: SetStateAction<T | undefined>, shouldUpdateUrl = true) => {
        // Mark that user has explicitly set a value
        hasUserSetValue.current = true

        // Resolve the SetStateAction to get the actual value
        const resolvedValue =
          typeof newValue === 'function'
            ? (newValue as (prev: T | undefined) => T | undefined)(valueRef.current)
            : newValue

        // Structural equals matches route-change sync and avoids redundant JSON.stringify on large values.
        if (equals(resolvedValue, valueRef.current)) {
          return
        }

        valueRef.current = resolvedValue
        setValue(resolvedValue)

        // Transform component state to URL format
        const urlValue = resolvedValue != null && transformToUrl != null ? transformToUrl(resolvedValue) : resolvedValue

        const params = Object.fromEntries(new URLSearchParams(location.search))
        const newParams: Record<string, string | string[] | undefined> = {
          ...params
        }

        const newQuery =
          (equals(resolvedValue, defaultValue) && removeWhenDefault) ||
          !shouldUpdateUrl ||
          (removeWhenEmpty && isNilOrEmpty(urlValue))
            ? omit([key], newParams)
            : { ...newParams, [key]: encodeQueryParameterValue(urlValue) }

        const newQueryString = new URLSearchParams(newQuery).toString()

        // check the length of the newQueryString and if it's greater than MAX_CHARACTERS_IN_QUERY_PARAMETER, don't update the query parameter
        if (newQueryString.length > MAX_CHARACTERS_IN_QUERY_PARAMETER) {
          return
        }

        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}${newQueryString === '' ? '' : '?' + newQueryString}`
        )
      },
      [defaultValue, key, removeWhenDefault, removeWhenEmpty, valueRef, transformToUrl]
    )

  return [value, setValueAndQueryParameter]
}
